// VexaDent nació en Ciudad Juárez; se conserva como respaldo únicamente
// para citas antiguas que todavía no guardan su zona de origen.
const FALLBACK_TIME_ZONE = "America/Chihuahua";

export function getDeviceTimeZone() {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      FALLBACK_TIME_ZONE
    );
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

function validTimeZone(timeZone) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format();
    return timeZone;
  } catch {
    return FALLBACK_TIME_ZONE;
  }
}

function datePartsInTimeZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: validTimeZone(timeZone),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return {
    fecha: `${values.year}-${values.month}-${values.day}`,
    hora: `${values.hour}:${values.minute}`,
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

export function getDateKeyInTimeZone(
  date = new Date(),
  timeZone = getDeviceTimeZone()
) {
  return datePartsInTimeZone(date, timeZone).fecha;
}

function offsetAt(instantMs, timeZone) {
  const parts = datePartsInTimeZone(new Date(instantMs), timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return asUtc - instantMs;
}

export function zonedDateTimeToDate(fecha, hora, timeZone = getDeviceTimeZone()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(fecha || ""))) return null;
  if (!/^\d{2}:\d{2}$/.test(String(hora || ""))) return null;

  const [year, month, day] = fecha.split("-").map(Number);
  const [hour, minute] = hora.split(":").map(Number);
  const wallClockMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  let guess = wallClockMs;

  for (let iteration = 0; iteration < 4; iteration += 1) {
    const next = wallClockMs - offsetAt(guess, timeZone);
    if (Math.abs(next - guess) < 1000) return new Date(next);
    guess = next;
  }

  return new Date(guess);
}

export function buildScheduledAt(fecha, hora, timeZone = getDeviceTimeZone()) {
  return zonedDateTimeToDate(fecha, hora, timeZone)?.toISOString() || null;
}

function asDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getScheduleLocalFields(entry, displayTimeZone = getDeviceTimeZone()) {
  if (!entry) return { fecha: "", hora: "" };

  const instant = asDate(entry.scheduledAt);
  if (instant) {
    const start = datePartsInTimeZone(instant, displayTimeZone);
    const endInstant = asDate(entry.scheduledEndAt);
    const end = endInstant
      ? datePartsInTimeZone(endInstant, displayTimeZone)
      : null;

    return {
      fecha: start.fecha,
      hora: start.hora,
      horaInicio: start.hora,
      horaFin: end?.hora || entry.horaFin || "",
      displayTimeZone,
    };
  }

  const sourceTimeZone =
    entry.timeZone ||
    entry.clinicTimeZone ||
    FALLBACK_TIME_ZONE;
  const sourceTime = entry.hora || entry.horaInicio || "";
  const start = zonedDateTimeToDate(
    String(entry.fecha || "").slice(0, 10),
    sourceTime,
    sourceTimeZone
  );

  if (!start) {
    return {
      fecha: String(entry.fecha || "").slice(0, 10),
      hora: entry.hora || entry.horaInicio || "",
      horaInicio: entry.horaInicio || entry.hora || "",
      horaFin: entry.horaFin || "",
      displayTimeZone,
    };
  }

  const startParts = datePartsInTimeZone(start, displayTimeZone);
  const end = entry.horaFin
    ? zonedDateTimeToDate(
        String(entry.fecha || "").slice(0, 10),
        entry.horaFin,
        sourceTimeZone
      )
    : null;
  const endParts = end ? datePartsInTimeZone(end, displayTimeZone) : null;

  return {
    fecha: startParts.fecha,
    hora: startParts.hora,
    horaInicio: startParts.hora,
    horaFin: endParts?.hora || entry.horaFin || "",
    displayTimeZone,
  };
}

export function localizeScheduleEntry(entry, displayTimeZone = getDeviceTimeZone()) {
  return {
    ...entry,
    ...getScheduleLocalFields(entry, displayTimeZone),
  };
}
